import { BagBilby } from '@/components/bilby';
import { StandardButton } from '@/components/buttons/standard';
import { ContentPanel } from '@/components/content-panel';
import { CurbyAccordion } from '@/components/curby-accordion';
import { CustomContentSection } from '@/components/custom-content-section';
import { SplashHeader } from '@/components/splash-header';
import { useLayoutContext } from '@/contexts';
import { useCurrentUser } from '@/hooks';
import { PrimaryLayout } from '@/layouts/primary';
import { Carousel } from '@mantine/carousel';
import { Flex, Space, Text, Title } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import {
	AccordionLocation,
	ContentSectionLocation,
	ContentSectionPlacement,
	RoleName,
} from '../../../../backend/src/types.generated';
import styles from './styles.module.css';

export const WelcomePage = () => {
	const navigate = useNavigate();
	const { currentUser } = useCurrentUser();
	const { isDesktop } = useLayoutContext();

	const signupButton = useMemo(
		() => (
			<StandardButton variant="filled" onClick={() => navigate({ to: '/signup' })}>
				Sign Up
			</StandardButton>
		),
		[navigate]
	);
	const loginButton = useMemo(
		() => (
			<StandardButton variant="light" onClick={() => navigate({ to: '/auth/login' })}>
				Log In
			</StandardButton>
		),
		[navigate]
	);

	if (currentUser?.roles.some((role) => role.name === RoleName.User)) {
		navigate({ to: '/home' });
	}

	return (
		<PrimaryLayout footer={{ children: [signupButton, loginButton] }} backgroundColor="white">
			<Flex direction="column" align="center" justify="flex-start" flex={1}>
				<SplashHeader>
					<Title order={1}>Welcome to Curby</Title>
				</SplashHeader>
				<CustomContentSection
					location={ContentSectionLocation.Welcome}
					placement={ContentSectionPlacement.Top}
				/>
				<Flex
					direction="column"
					justify="center"
					align="center"
					flex={1}
					w="100%"
					pt={isDesktop ? 0 : 0}
					mt={isDesktop ? -200 : -100}
					gap={0}
				>
					<Space h={70} />
					<BagBilby className={styles.bilbyImage} />
					<ContentPanel noPad>
						<Carousel pt="lg" withIndicators withControls={false} classNames={styles}>
							<Carousel.Slide>
								<Title order={2} mb="sm">
									Curby
								</Title>
								<Text ta="center" size={isDesktop ? 'md' : 'sm'} w="90%">
									The Curby program helps Aussies to keep hard to recycle materials such as soft
									plastics out of the environment. Through using special program bags that are
									placed in your yellow-lidded bin (where council-approved) or your nearest
									return-to-store drop-off point, and incentivising positive household recycling
									behaviour through the Curby rewards program.
								</Text>
							</Carousel.Slide>
							<Carousel.Slide>
								<Title order={2} mb="md">
									Curby Rewards
								</Title>
								<Text ta="center" size={isDesktop ? 'md' : 'sm'} w="90%">
									The Curby Rewards program is Australia’s first and only customer loyalty program
									that rewards consumers for doing their part to recycle specific materials.
								</Text>
							</Carousel.Slide>
							<Carousel.Slide>
								<Title order={2} mb="md">
									How it works
								</Title>

								<Text ta="center" size={isDesktop ? 'md' : 'sm'} w="90%">
									You earn entries to the Curby Rewards prize draw every time you scan and drop a
									CurbyBag into a yellow-lidded bin (where council-approved) or your nearest
									return-to-store drop-off point. So, the more you recycle the more chances you’ll
									have to win!
								</Text>
							</Carousel.Slide>
							<Carousel.Slide>
								<Text ta="center" size={isDesktop ? 'md' : 'sm'} w="90%">
									To find out more about the Curby Program, click sign up below!
								</Text>
							</Carousel.Slide>
						</Carousel>
					</ContentPanel>
				</Flex>
				<CustomContentSection
					location={ContentSectionLocation.Welcome}
					placement={ContentSectionPlacement.Middle}
				/>
				<CurbyAccordion title="" accordionLocation={AccordionLocation.WelcomePage} />
				<CustomContentSection
					location={ContentSectionLocation.Welcome}
					placement={ContentSectionPlacement.Bottom}
				/>
				{isDesktop && (
					<Flex direction="row" pt="lg" pb="xl" justify="center" align="center" gap="xl">
						<Flex gap="lg" w={500}>
							{signupButton}
							{loginButton}
						</Flex>
					</Flex>
				)}
			</Flex>
		</PrimaryLayout>
	);
};
